import { MarkerType } from "reactflow";
import { withEdgeStyles } from "./utils/edgeStyles";

export const transformBackendToReactFlow = (backendFlow, handleDelete, handleDuplicate, handleEdit) => {
    if (!backendFlow || !backendFlow.nodes) return { nodes: [], edges: [] };

    const reactFlowNodes = [];
    const reactFlowEdges = [];

    // Convert snake_case trigger_event back to readable format
    const formatEventName = (snakeCase) => {
        if (!snakeCase) return '';
        return snakeCase
            .split('_')
            .map(word => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ');
    };

    // Create a map of node indices to their IDs
    const nodeIdsByIndex = {};

    // Counter for end nodes to give them unique IDs
    let endNodeCounter = 0;

    // Process all nodes from backend
    backendFlow.nodes.forEach((node, index) => {
        const nodeId = node.type === 'start'
            ? 'start-node'
            : node.type === 'end'
                ? `end-node-${endNodeCounter++}` // Changed to include counter
                : `${node.type}-${Date.now()}-${index}`;

        nodeIdsByIndex[index] = nodeId;

        const position = node.position || { x: 250, y: 50 + (index * 200) };


        // Handle start node
        if (node.type === 'start') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'startNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Campaign Start',
                    triggerType: node.config?.trigger_type || 'event',

                    // API trigger fields
                    apiAuthMethod: node.config?.api_auth_method || 'bearer',

                    // Event trigger fields
                    eventName: node.config?.event_name || '',
                    eventTriggerMode: node.config?.event_trigger_mode || 'new_event',
                    eventProperties: node.config.eventProperties || [],

                    // Customer segment fields
                    customerSegmentId: node.config?.customerSegmentId || '',
                    segmentEntryMode: node.config?.segmentEntryMode || 'existing',
                    segmentExitOnMatch: node.config?.segmentExitOnMatch || false,
                    customerCount: node.config?.customerCount || 0,
                    allCustomers: node.config?.allCustomers || false,
                    segmentName: node.config?.segmentName || '',

                    // Schedule fields
                    enableSchedule: node.config?.enableSchedule || false,
                    scheduleType: node.config?.scheduleType || 'daily',
                    scheduleTime: node.config?.scheduleTime || '09:00',
                    scheduleTimezone: node.config?.scheduleTimezone || 'Asia/Kolkata',
                    scheduleDayOfWeek: node.config?.scheduleDayOfWeek || 'monday',
                    scheduleDayOfMonth: node.config?.scheduleDayOfMonth || '1',

                    // Trigger payload contract fields
                    triggerContractFields: node.config?.trigger_contract_fields || [],
                    triggerSampleJson: node.config?.trigger_sample_json || '',

                    onEdit: handleEdit,
                    onDelete: handleDelete,
                },
            });
        }

        // Handle end node
        else if (node.type === 'end') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'endNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'End',
                    onDelete: handleDelete,
                },
            });
        }
        // Handle action node
        else if (node.type === 'action') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'actionNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Send Message',
                    channel: node.config?.channel?.toString() || '',
                    templateId: node.config?.template_id?.toString() || '',
                    // template: node.config?.message || '',
                    variableMappings: node.config?.variable_mappings || {},
                    messageType: node.config?.message_type || 'template',
                    template: node.config?.message_type === 'template' ? (node.config?.message || '') : '',
                    freeformMessage: node.config?.message_type === 'freeform' ? (node.config?.message || '') : '',

                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }

        else if (node.type === 'customer_profile') {
            // Restore conditions from backend payload.
            // Falls back to legacy single-field shape for backward compatibility.
            const rawConditions =
                node.config?.conditions && node.config.conditions.length > 0
                    ? node.config.conditions
                    : node.config?.profile_key
                        ? [
                            {
                                profile_key: node.config.profile_key,
                                operator: node.config.operator || 'equals',
                                match_value: node.config.match_value || '',
                            },
                        ]
                        : [];

            reactFlowNodes.push({
                id: nodeId,
                type: 'customerProfileNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Check Customer Profile',
                    conditions: rawConditions,
                    trueLabel: node.config?.true_label || 'Match Found (True)',
                    falseLabel: node.config?.false_label || 'No Match (False)',
                    // Attach event handlers — replace with your actual handler references
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }


        // Handle wait/delay node
        else if (node.type === 'wait') {
            const durationSeconds = node.config?.duration_seconds || 0;
            let duration = durationSeconds;
            let unit = 'seconds';

            if (durationSeconds >= 604800 && durationSeconds % 604800 === 0) {
                duration = Math.floor(durationSeconds / 604800);
                unit = 'weeks';
            } else if (durationSeconds >= 86400 && durationSeconds % 86400 === 0) {
                duration = Math.floor(durationSeconds / 86400);
                unit = 'days';
            } else if (durationSeconds >= 3600 && durationSeconds % 3600 === 0) {
                duration = Math.floor(durationSeconds / 3600);
                unit = 'hours';
            } else if (durationSeconds >= 60 && durationSeconds % 60 === 0) {
                duration = Math.floor(durationSeconds / 60);
                unit = 'minutes';
            }

            reactFlowNodes.push({
                id: nodeId,
                type: 'delayNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Wait for Time',
                    duration: duration.toString(),
                    unit: unit,
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        else if (node.type === 'check_event') {
            const lookbackSeconds = node.config?.lookback_seconds || 86400;
            let timeWindowValue = 1;
            let timeWindowUnit = 'days';

            if (lookbackSeconds >= 604800 && lookbackSeconds % 604800 === 0) {
                timeWindowValue = Math.floor(lookbackSeconds / 604800);
                timeWindowUnit = 'weeks';
            } else if (lookbackSeconds >= 86400 && lookbackSeconds % 86400 === 0) {
                timeWindowValue = Math.floor(lookbackSeconds / 86400);
                timeWindowUnit = 'days';
            } else if (lookbackSeconds >= 3600 && lookbackSeconds % 3600 === 0) {
                timeWindowValue = Math.floor(lookbackSeconds / 3600);
                timeWindowUnit = 'hours';
            } else if (lookbackSeconds >= 60 && lookbackSeconds % 60 === 0) {
                timeWindowValue = Math.floor(lookbackSeconds / 60);
                timeWindowUnit = 'minutes';
            }

            const attributeMatching = node.config?.attribute_matching || {};
            const hasAttributeMatching = !!attributeMatching.target_attribute;

            // Determine condition logic based on what's present in config
            let conditionLogic = 'occurred'; // default

            if (node.config?.count_condition) {
                conditionLogic = 'count_of_events';
            } else if (node.config?.attribute_check) {
                conditionLogic = 'attribute_check';
            } else if (node.config?.lookback_seconds) {
                conditionLogic = 'within_time';
            }

            // Determine reference type for attribute matching
            let referenceType = 'static';
            if (attributeMatching.compare_against === 'previous_event_attribute') {
                referenceType = 'previous_event';
            } else if (attributeMatching.compare_against === 'message_variable') {
                referenceType = 'message_variable';
            }

            // Format previous event reference
            let previousEventRef = '';
            if (referenceType === 'previous_event' && attributeMatching.reference_event && attributeMatching.reference_attribute) {
                previousEventRef = `${formatEventName(attributeMatching.reference_event)}.${attributeMatching.reference_attribute}`;
            }

            reactFlowNodes.push({
                id: nodeId,
                type: 'conditionNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Event Check',
                    eventSource: formatEventName(node.config?.event_name) || '',
                    conditionLogic: conditionLogic,
                    yesLabel: 'Yes',
                    noLabel: 'No',
                    fallbackEnabled: node.config?.fallback_enabled || false, // Load fallback

                    // Time window fields
                    timeWindowValue: timeWindowValue.toString(),
                    timeWindowUnit: timeWindowUnit,

                    // Attribute check fields
                    attributeName: node.config?.attribute_check?.attribute_name || '',
                    operator: node.config?.attribute_check?.operator || 'equals',
                    attributeValue: node.config?.attribute_check?.attribute_value || '',

                    // Count of Events fields
                    countOperator: node.config?.count_condition?.operator || 'equals',
                    countValue: node.config?.count_condition?.value?.toString() || '',

                    // Attribute matching fields
                    enableAttributeMatching: hasAttributeMatching,
                    matchAttribute: attributeMatching.target_attribute || '',
                    comparisonType: attributeMatching.operator || 'equals',
                    referenceType: referenceType,
                    previousEventRef: previousEventRef,
                    messageVariable: attributeMatching.message_variable || '',
                    staticValue: attributeMatching.fixed_value || '',
                    attributeFallbackEnabled: attributeMatching.on_missing_attribute === 'no_path',

                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        // Handle api_call node
        else if (node.type === 'api_call') {
            // Convert custom_headers object back to array format for the UI
            let headersArray = [{ key: '', value: '' }];
            if (node.config?.custom_headers && typeof node.config.custom_headers === 'object' && Object.keys(node.config.custom_headers).length > 0) {
                headersArray = Object.entries(node.config.custom_headers).map(([key, value]) => ({ key, value }));
            }

            const responseMappingArray = (node.config?.response_mapping || []).map(m => ({
                responseField: m.response_field || '',
                saveAs: m.save_as || '',
            }));
            if (responseMappingArray.length === 0) {
                responseMappingArray.push({ responseField: '', saveAs: '' });
            }

            let apiPayloadStr = '';
            if (node.config?.request_payload) {
                apiPayloadStr = typeof node.config.request_payload === 'object'
                    ? JSON.stringify(node.config.request_payload, null, 2)
                    : node.config.request_payload;
            }

            const executionMode = node.config?.execution_mode || 'sync';

            reactFlowNodes.push({
                id: nodeId,
                type: 'apiNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.config?.step_name || node.name || 'API Call',
                    apiMethod: node.config?.http_method || 'GET',
                    apiEndpoint: node.config?.api_endpoint || '',
                    authType: node.config?.auth_type || 'none',
                    apiKey: node.config?.api_key || '',
                    authHeaderKey: node.config?.auth_header_key || 'X-API-Key',
                    bearerToken: node.config?.bearer_token || '',
                    basicUsername: node.config?.basic_username || '',
                    basicPassword: node.config?.basic_password || '',
                    executionMode: executionMode,
                    timeout: node.config?.timeout || 30,
                    responseKey: node.config?.response_key || 'api_response',
                    queryParams: node.config?.query_params || [],
                    headers: headersArray,
                    bodyType: node.config?.body_type || 'json',
                    apiPayload: apiPayloadStr,
                    responseMapping: responseMappingArray,
                    handle4xx: node.config?.handle_4xx || false,
                    handle5xx: node.config?.handle_5xx || false,
                    retryEnabled: node.config?.retry_enabled || false,
                    retryAttempts: node.config?.retry_attempts || 3,
                    retryStrategy: node.config?.retry_strategy || 'exponential',
                    responseNamespace: node.config?.response_namespace || 'api_response',
                    contractFields: node.config?.contract_fields || [],
                    sampleJson: node.config?.sample_json || '',
                    // Legacy
                    method: node.config?.http_method || 'GET',
                    endpoint: node.config?.api_endpoint || '',
                    payload: apiPayloadStr,
                    customHeaders: typeof node.config?.custom_headers === 'object'
                        ? JSON.stringify(node.config.custom_headers, null, 2)
                        : node.config?.custom_headers || '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });

         
        }
        // NEW: Handle A/B Test node
        else if (node.type === 'ab_test') {
            const variants = node.config?.variants || [];
            const numBranches = node.config?.num_branches || 2;

            // Reconstruct branches object from variants
            const branches = {};
            variants.forEach(variant => {
                branches[variant.id] = variant.percentage;
            });

            reactFlowNodes.push({
                id: nodeId,
                type: 'abTestNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.config?.step_name || node.name || 'A/B Test',
                    numBranches: numBranches,
                    branches: branches,
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        // Handle multi_step node
        else if (node.type === 'multi_step') {
            const stepCount = node.config?.step_count || 2;
            const stepNames = node.config?.step_names || Array.from({ length: stepCount }, (_, i) => `Step ${i + 1}`);
            reactFlowNodes.push({
                id: nodeId,
                type: 'multiStepNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.config?.step_name || node.name || 'Multi Step',
                    stepCount,
                    stepNames,
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        // Handle merge node
        else if (node.type === 'merge') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'mergeNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.config?.step_name || node.name || 'Merge Branches',
                    inputCount: node.config?.input_count || 3,
                    mergeBehavior: node.config?.merge_behavior || 'any',
                    continueOnce: node.config?.continue_once !== false,
                    timeoutEnabled: node.config?.timeout_enabled || false,
                    timeoutValue: node.config?.timeout_value || 30,
                    timeoutUnit: node.config?.timeout_unit || 'minutes',
                    continueOnTimeout: node.config?.continue_on_timeout || false,
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        // NEW: Handle Message Read node
        // Handle message_read node
        else if (node.type === 'message_read') {
            const waitSeconds = node.config?.wait_seconds || 86400;
            let waitTime = node.config?.wait_time || 24;
            let waitUnit = node.config?.wait_unit || 'hours';

            // If wait_time and wait_unit not provided, calculate from wait_seconds
            if (!node.config?.wait_time) {
                if (waitSeconds >= 86400 && waitSeconds % 86400 === 0) {
                    waitTime = Math.floor(waitSeconds / 86400);
                    waitUnit = 'days';
                } else if (waitSeconds >= 3600 && waitSeconds % 3600 === 0) {
                    waitTime = Math.floor(waitSeconds / 3600);
                    waitUnit = 'hours';
                } else if (waitSeconds >= 60 && waitSeconds % 60 === 0) {
                    waitTime = Math.floor(waitSeconds / 60);
                    waitUnit = 'minutes';
                }
            }

            // Determine tracking mode from character_limit if not explicitly set
            let trackingMode = node.config?.tracking_mode || 'read';
            if (!node.config?.tracking_mode && node.config?.character_limit) {
                trackingMode = node.config.character_limit === 200 ? 'delivered' : 'read';
            }

            reactFlowNodes.push({
                id: nodeId,
                type: 'messageReadNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Message Read',
                    waitTime: waitTime,
                    waitUnit: waitUnit,
                    trackingMode: trackingMode,
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        // NEW: Handle Update Profile node
        else if (node.type === 'update_profile') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'updateProfileNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Update Customer Profile',
                    tagName: node.config?.tag_name || '',
                    // tagValue: node.config?.tag_value || '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }

        // Handle check_channel_consent node
        else if (node.type === 'check_channel_consent') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'checkChannelConsentNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.config?.step_name || node.name || 'Check Channel Consent',
                    channel: node.config?.channel?.toString() || '',
                    channelLabel: node.config?.channel_label || '',
                    enabledLabel: node.config?.enabled_label || 'Enabled',
                    disabledLabel: node.config?.disabled_label || 'Disabled',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }

        // Handle update_channel_consent node
        else if (node.type === 'update_channel_consent') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'updateChannelConsentNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.config?.step_name || node.name || 'Update Channel Consent',
                    channel: node.config?.channel?.toString() || '',
                    channelLabel: node.config?.channel_label || '',
                    consentAction: node.config?.consent_action || '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }

        // Inside the backendFlow.nodes.forEach loop, add these cases:

        // Handle dynamic_wait node
        else if (node.type === 'dynamic_wait') {
            const fallbackSeconds = node.config?.fallback_duration_seconds || 86400;
            let fallbackDuration = fallbackSeconds;
            let fallbackUnit = 'seconds';

            if (fallbackSeconds >= 604800 && fallbackSeconds % 604800 === 0) {
                fallbackDuration = Math.floor(fallbackSeconds / 604800);
                fallbackUnit = 'weeks';
            } else if (fallbackSeconds >= 86400 && fallbackSeconds % 86400 === 0) {
                fallbackDuration = Math.floor(fallbackSeconds / 86400);
                fallbackUnit = 'days';
            } else if (fallbackSeconds >= 3600 && fallbackSeconds % 3600 === 0) {
                fallbackDuration = Math.floor(fallbackSeconds / 3600);
                fallbackUnit = 'hours';
            } else if (fallbackSeconds >= 60 && fallbackSeconds % 60 === 0) {
                fallbackDuration = Math.floor(fallbackSeconds / 60);
                fallbackUnit = 'minutes';
            }

            const maxWaitSeconds = node.config?.max_wait_duration_seconds || 2592000;
            let maxWaitDuration = maxWaitSeconds;
            let maxWaitUnit = 'seconds';

            if (maxWaitSeconds >= 604800 && maxWaitSeconds % 604800 === 0) {
                maxWaitDuration = Math.floor(maxWaitSeconds / 604800);
                maxWaitUnit = 'weeks';
            } else if (maxWaitSeconds >= 86400 && maxWaitSeconds % 86400 === 0) {
                maxWaitDuration = Math.floor(maxWaitSeconds / 86400);
                maxWaitUnit = 'days';
            } else if (maxWaitSeconds >= 3600 && maxWaitSeconds % 3600 === 0) {
                maxWaitDuration = Math.floor(maxWaitSeconds / 3600);
                maxWaitUnit = 'hours';
            }


            reactFlowNodes.push({
                id: nodeId,
                type: 'dynamicDelayNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Wait Time (Dynamic)',
                    delaySubtype: node.config.delay_type,
                    type: node.config.delay_type,
                    dynamicWaitSource: node.config?.dynamic_wait_source || 'event',
                    // dynamicEventFieldType: node.config?.event_field_type ,
                    dynamicEventFieldPath: node.config?.event_field_path,
                    dynamicApiNode: node.config?.api_node_id || '',
                    dynamicApiField: node.config?.api_response_field,
                    dynamicExpression: node.config?.expression || '',
                    fallbackDuration: fallbackDuration.toString(),
                    fallbackUnit: fallbackUnit,
                    maxWaitDuration: maxWaitDuration.toString(),
                    maxWaitUnit: maxWaitUnit,
                    duration: node.config?.duration || '',
                    unit: node.config?.unit || 'days',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        else if (node.type === 'dtmf_response') {
            const dtmfConfig = node.config?.dtmf_responses || [];

            const dtmfResponses = dtmfConfig.map((resp, index) => ({
                key: resp.dtmf_key,
                label: resp.label,
                id: resp.response_id || `dtmf_${index + 1}`,
                timeoutEnabled: resp.timeout_enabled || false,
                timeoutDuration: resp.timeout_duration || 30,
                timeoutUnit: resp.timeout_unit || 'seconds',
            }));

            reactFlowNodes.push({
                id: nodeId,
                type: 'dtmfNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'DTMF Response',
                    dtmfResponses: dtmfResponses,
                    noResponseWaitEnabled: node.config?.no_response_enabled || false,
                    noResponseWaitTime: node.config?.no_response_time || 30,
                    noResponseWaitUnit: node.config?.no_response_unit || 'seconds',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }

        else if (node.type === 'sms_inbound') {
            const smsConfig = node.config?.sms_responses || [];

            const smsResponses = smsConfig.map((resp, index) => ({
                key: resp.sms_key,
                label: resp.label,
                id: resp.response_id || `sms_${index + 1}`,
                timeoutEnabled: resp.timeout_enabled || false,
                timeoutDuration: resp.timeout_duration || 30,
                timeoutUnit: resp.timeout_unit || 'seconds',
            }));

            reactFlowNodes.push({
                id: nodeId,
                type: 'smsInboundNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'SMS Inbound',
                    smsResponses: smsResponses,
                    noResponseWaitEnabled: node.config?.no_response_enabled || false,
                    noResponseWaitTime: node.config?.no_response_time || 24,
                    noResponseWaitUnit: node.config?.no_response_unit || 'hours',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }

        else if (node.type === 'smart_condition') {
            const groups = (node.config?.groups || []).map((g, gi) => ({
                id: g.id || `group_${gi}`,
                label: g.label || `Group ${gi + 1}`,
                color: g.color || 'blue',
                rules: (g.rules || []).map((r, ri) => ({
                    id: `rule_${Date.now()}_${gi}_${ri}`,
                    variable: r.variable_path || '',
                    variablePath: r.variable_path || '',
                    variableType: r.variable_type || 'string',
                    operator: r.operator || 'equals',
                    value: r.value || '',
                })),
            }));

            reactFlowNodes.push({
                id: nodeId,
                type: 'smartConditionNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Smart Condition',
                    groups: groups,
                    defaultLabel: node.config?.default_label || 'No Match',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }

        else if (node.type === 'enhanced_condition') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'enhancedConditionNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Enhanced Condition',
                    conditionType: node.config?.condition_type || 'conversion',
                    conversionDefinition: node.config?.conversion_definition || '',
                    matchEntityBy: node.config?.match_entity_by || 'order_id',
                    journeyState: node.config?.journey_state || '',
                    attributeSource: node.config?.attribute_source || 'profile',
                    fieldSelector: node.config?.field_selector || '',
                    eventName: formatEventName(node.config?.event_name) || '',
                    countOperator: node.config?.count_operator || '>',
                    countValue: node.config?.count_value?.toString() || '0',
                    yesLabel: 'Yes',
                    noLabel: 'No',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }

        else if (node.type === 'ivr_call') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'ivrNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'IVR Call',
                    templateId: node.config?.template_id || null,
                    templateName: node.config?.template_name || '',
                    voiceScript: node.config?.voice_script || '',
                    ivrVariableMappings: node.config?.variable_mappings || {},
                    enableRetries: node.config?.enable_retries || false,
                    noOfRetries: node.config?.no_of_retries || 3,
                    waitTimeBetweenRetries: node.config?.wait_time_between_retries || 5,
                    waitTimeUnit: node.config?.wait_time_unit || 'minutes',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        else if (node.type === 'call_flow') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'callFlowNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Call Flow',
                    selectedFlowId: node.config?.flow_id || '',
                    selectedFlowName: node.config?.flow_name || '',
                    flowStatus: node.config?.flow_status || '',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        else if (node.type === 'whatsapp_call_flow') {
            reactFlowNodes.push({
                id: nodeId,
                type: 'callWhatsappFlowNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Call WhatsApp Flow',
                    flowId: node.config?.flow_id || '',
                    bodyText: node.config?.body_text || '',
                    kvPairs: (node.config?.parameters || []).map(p => ({ key: p.key, value: p.value })),
                    timeoutEnabled: node.config?.timeout_enabled || false,
                    timeoutWaitTime: node.config?.timeout_time || 30,
                    timeoutWaitUnit: node.config?.timeout_unit || 'minutes',
                    timeoutWaitReference: node.config?.timeout_reference || 'flow_trigger',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
        else if (node.type === 'button_clicked') {
            const buttonConfig = node.config?.buttons || [];

            // Transform backend buttons to frontend format
            const buttons = buttonConfig.map((btn, index) => ({
                name: btn.button_name,
                id: btn.button_id || `button_${index + 1}`,
                conditionEnabled: btn.condition_enabled || false,
                conditionTime: btn.condition_time || 60,
                conditionUnit: btn.condition_unit || 'minutes',
                conditionReference: btn.condition_reference || 'message_sent',
            }));

            // Ensure we always have 3 button slots
            while (buttons.length < 3) {
                buttons.push({
                    name: '',
                    id: `button_${buttons.length + 1}`,
                    conditionEnabled: false,
                    conditionTime: 60,
                    conditionUnit: 'minutes',
                    conditionReference: 'message_sent'
                });
            }

            reactFlowNodes.push({
                id: nodeId,
                type: 'buttonClickedNode',
                position: position,
                data: {
                    id: nodeId,
                    label: node.name || 'Button Clicked',
                    buttons: buttons,
                    noClickWaitEnabled: node.config?.no_click_enabled || false,
                    noClickWaitTime: node.config?.no_click_time || 60,
                    noClickWaitUnit: node.config?.no_click_unit || 'minutes',
                    noClickWaitReference: node.config?.no_click_reference || 'message_sent',
                    onEdit: handleEdit,
                    onDelete: handleDelete,
                    onDuplicate: handleDuplicate,
                },
            });
        }
    });

    // Create edges based on next_node_index and branch indices
    backendFlow.nodes.forEach((node, index) => {
        const sourceNodeId = nodeIdsByIndex[index];

        if (node.type === 'check_event') {
            // Create yes branch edge
            const yesBranchIndex = node.config?.yes_branch_index;
            if (yesBranchIndex !== undefined && yesBranchIndex !== null) {
                const targetId = nodeIdsByIndex[yesBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-yes-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'yes',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: 'Yes',
                    });
                }
            }

            // Create no branch edge
            const noBranchIndex = node.config?.no_branch_index;
            if (noBranchIndex !== undefined && noBranchIndex !== null) {
                const targetId = nodeIdsByIndex[noBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-no-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'no',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: 'No',
                    });
                }
            }
        }
        // NEW: Handle A/B Test branches
        else if (node.type === 'ab_test') {
            const variants = node.config?.variants || [];
            variants.forEach(variant => {
                const branchIndex = variant.branch_index;
                if (branchIndex !== undefined && branchIndex !== null && branchIndex !== -1) {
                    const targetId = nodeIdsByIndex[branchIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-${variant.id.toLowerCase()}-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            sourceHandle: variant.id.toLowerCase(), // 'a', 'b', 'c', 'd', 'e'
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: variant.id,
                        });
                    }
                }
            });
        }
        // Handle merge node incoming branches + outgoing edge
        else if (node.type === 'merge') {
            // Incoming: one edge per branch, connected to the correct merge-in-N handle
            const branches = node.config?.branches || [];
            branches.forEach(branch => {
                const srcId = nodeIdsByIndex[branch.source_node_index];
                if (srcId) {
                    // Use stored source_handle, or derive it from the source node's config
                    let sourceHandle = branch.source_handle || null;
                    if (!sourceHandle) {
                        const srcNode = backendFlow.nodes[branch.source_node_index];
                        if (srcNode?.type === 'button_clicked') {
                            if (srcNode.config?.no_click_branch_index === index) {
                                sourceHandle = 'no_click';
                            } else {
                                const btn = srcNode.config?.buttons?.find(b => b.branch_index === index);
                                if (btn) sourceHandle = `${btn.button_id}_clicked`;
                            }
                        } else if (srcNode?.type === 'dtmf_response') {
                            if (srcNode.config?.no_response_branch_index === index) {
                                sourceHandle = 'no_response';
                            } else {
                                const r = srcNode.config?.dtmf_responses?.find(d => d.branch_index === index);
                                if (r) sourceHandle = `${r.response_id}_received`;
                            }
                        } else if (srcNode?.type === 'sms_inbound') {
                            if (srcNode.config?.no_response_branch_index === index) {
                                sourceHandle = 'no_response';
                            } else {
                                const r = srcNode.config?.sms_responses?.find(s => s.branch_index === index);
                                if (r) sourceHandle = `${r.response_id}_received`;
                            }
                        }
                    }
                    reactFlowEdges.push({
                        id: `edge-${srcId}-merge-in-${branch.branch_index}-${sourceNodeId}`,
                        source: srcId,
                        sourceHandle,
                        target: sourceNodeId,
                        targetHandle: `merge-in-${branch.branch_index}`,
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                    });
                }
            });
            // Outgoing: merge → next node (e.g. end)
            const nextNodeIndex = node.next_node_index;
            if (nextNodeIndex !== undefined && nextNodeIndex !== null && nextNodeIndex !== -1) {
                const targetId = nodeIdsByIndex[nextNodeIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-merge-out-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'merge-out',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                    });
                }
            }
        }
        // Handle multi_step branches
        else if (node.type === 'multi_step') {
            const steps = node.config?.steps || [];
            steps.forEach(step => {
                const branchIndex = step.branch_index;
                if (branchIndex !== undefined && branchIndex !== null && branchIndex !== -1) {
                    const targetId = nodeIdsByIndex[branchIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-step-${step.step_index}-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            sourceHandle: `step-${step.step_index}`,
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: step.label || `Step ${step.step_index + 1}`,
                        });
                    }
                }
            });
        }
        // NEW: Handle Message Read branches
        else if (node.type === 'message_read') {
            // Create yes branch edge (message read)
            const yesBranchIndex = node.config?.yes_branch_index;
            if (yesBranchIndex !== undefined && yesBranchIndex !== null) {
                const targetId = nodeIdsByIndex[yesBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-yes-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'yes',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: 'Read',
                    });
                }
            }

            // Create no branch edge (message not read)
            const noBranchIndex = node.config?.no_branch_index;
            if (noBranchIndex !== undefined && noBranchIndex !== null) {
                const targetId = nodeIdsByIndex[noBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-no-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'no',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: 'Not Read',
                    });
                }
            }
        }
        else if (node.type === 'button_clicked') {
            const buttons = node.config?.buttons || [];

            buttons.forEach(button => {
                if (button.condition_enabled) {
                    // Handle true branch (clicked in time)
                    const trueBranchIndex = button.true_branch_index;
                    if (trueBranchIndex !== undefined && trueBranchIndex !== null && trueBranchIndex !== -1) {
                        const targetId = nodeIdsByIndex[trueBranchIndex];
                        if (targetId) {
                            reactFlowEdges.push({
                                id: `edge-${sourceNodeId}-${button.button_id}-true-${targetId}`,
                                source: sourceNodeId,
                                target: targetId,
                                sourceHandle: `${button.button_id}_true`,
                                type: 'smoothstep',
                                animated: true,
                                markerEnd: { type: MarkerType.ArrowClosed },
                                label: `${button.button_name} ✓`,
                            });
                        }
                    }

                    // Handle false branch (not clicked in time)
                    const falseBranchIndex = button.false_branch_index;
                    if (falseBranchIndex !== undefined && falseBranchIndex !== null && falseBranchIndex !== -1) {
                        const targetId = nodeIdsByIndex[falseBranchIndex];
                        if (targetId) {
                            reactFlowEdges.push({
                                id: `edge-${sourceNodeId}-${button.button_id}-false-${targetId}`,
                                source: sourceNodeId,
                                target: targetId,
                                sourceHandle: `${button.button_id}_false`,
                                type: 'smoothstep',
                                animated: true,
                                markerEnd: { type: MarkerType.ArrowClosed },
                                label: `${button.button_name} ✗`,
                            });
                        }
                    }
                } else {
                    // Simple button click without condition
                    const branchIndex = button.branch_index;
                    if (branchIndex !== undefined && branchIndex !== null && branchIndex !== -1) {
                        if (backendFlow.nodes[branchIndex]?.type === 'merge') return;
                        const targetId = nodeIdsByIndex[branchIndex];
                        if (targetId) {
                            reactFlowEdges.push({
                                id: `edge-${sourceNodeId}-${button.button_id}-clicked-${targetId}`,
                                source: sourceNodeId,
                                target: targetId,
                                sourceHandle: `${button.button_id}_clicked`,
                                type: 'smoothstep',
                                animated: true,
                                markerEnd: { type: MarkerType.ArrowClosed },
                                label: button.button_name,
                            });
                        }
                    }
                }
            });

            // Handle no_click branch if enabled
            if (node.config?.no_click_enabled && node.config?.no_click_branch_index !== undefined) {
                const noClickIndex = node.config.no_click_branch_index;
                if (noClickIndex !== -1 && backendFlow.nodes[noClickIndex]?.type !== 'merge') {
                    const targetId = nodeIdsByIndex[noClickIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-no_click-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            sourceHandle: 'no_click',
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: 'No Click',
                        });
                    }
                }
            }
        }

        else if (node.type === 'dtmf_response') {
            const dtmfResponses = node.config?.dtmf_responses || [];

            dtmfResponses.forEach(response => {
                const branchIndex = response.branch_index;
                if (branchIndex !== undefined && branchIndex !== null && branchIndex !== -1) {
                    if (backendFlow.nodes[branchIndex]?.type === 'merge') return;
                    const targetId = nodeIdsByIndex[branchIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-${response.response_id}-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            sourceHandle: `${response.response_id}_received`,
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: response.label,
                        });
                    }
                }
            });

            // Handle no_response branch if enabled
            if (node.config?.no_response_enabled && node.config?.no_response_branch_index !== undefined) {
                const noResponseIndex = node.config.no_response_branch_index;
                if (noResponseIndex !== -1 && backendFlow.nodes[noResponseIndex]?.type !== 'merge') {
                    const targetId = nodeIdsByIndex[noResponseIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-no_response-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            sourceHandle: 'no_response',
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: 'No Response',
                        });
                    }
                }
            }
        }

        else if (node.type === 'sms_inbound') {
            const smsResponses = node.config?.sms_responses || [];

            smsResponses.forEach(response => {
                const branchIndex = response.branch_index;
                if (branchIndex !== undefined && branchIndex !== null && branchIndex !== -1) {
                    if (backendFlow.nodes[branchIndex]?.type === 'merge') return;
                    const targetId = nodeIdsByIndex[branchIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-${response.response_id}-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            sourceHandle: `${response.response_id}_received`,
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: response.label,
                        });
                    }
                }
            });

            // Handle no_response branch
            if (node.config?.no_response_enabled && node.config?.no_response_branch_index !== undefined) {
                const noResponseIndex = node.config.no_response_branch_index;
                if (noResponseIndex !== -1 && backendFlow.nodes[noResponseIndex]?.type !== 'merge') {
                    const targetId = nodeIdsByIndex[noResponseIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-no_response-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            sourceHandle: 'no_response',
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: 'No Response',
                        });
                    }
                }
            }
        }

        else if (node.type === 'api_call') {
            const executionMode = node.config?.execution_mode || 'sync';

            if (executionMode === 'sync') {
                // Success branch edge
                const successIndex = node.config?.success_branch_index;
                if (successIndex !== undefined && successIndex !== null && successIndex >= 0) {
                    const targetId = nodeIdsByIndex[successIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-success-${targetId}`,
                            source: sourceNodeId,
                            sourceHandle: 'success',
                            target: targetId,
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: 'Success',
                            labelStyle: { fontSize: 10, fill: '#38A169' },
                        });
                    }
                }

                // Error branch edge
                const errorIndex = node.config?.error_branch_index;
                if (errorIndex !== undefined && errorIndex !== null && errorIndex >= 0) {
                    const targetId = nodeIdsByIndex[errorIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-error-${targetId}`,
                            source: sourceNodeId,
                            sourceHandle: 'error',
                            target: targetId,
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: 'Error',
                            labelStyle: { fontSize: 10, fill: '#E53E3E' },
                        });
                    }
                }
            } else {
                // Async mode: single next_node_index
                const nextNodeIndex = node.next_node_index;
                if (nextNodeIndex !== undefined && nextNodeIndex !== null && nextNodeIndex !== -1) {
                    // Skip edges whose target is a merge node — those are reconstructed
                    // from merge.config.branches so they land on the correct merge-in-N handle
                    const targetNode = backendFlow.nodes[nextNodeIndex];
                    if (targetNode?.type !== 'merge') {
                        const targetId = nodeIdsByIndex[nextNodeIndex];
                        if (targetId) {
                            reactFlowEdges.push({
                                id: `edge-${sourceNodeId}-${targetId}`,
                                source: sourceNodeId,
                                target: targetId,
                                type: 'smoothstep',
                                animated: true,
                                markerEnd: { type: MarkerType.ArrowClosed },
                            });
                        }
                    }
                }
            }
        }
        else if (node.type === 'enhanced_condition') {
            // Yes branch
            const yesBranchIndex = node.config?.yes_branch_index;
            if (yesBranchIndex !== undefined && yesBranchIndex !== null) {
                const targetId = nodeIdsByIndex[yesBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-yes-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'yes',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: 'Yes',
                    });
                }
            }

            // No branch
            const noBranchIndex = node.config?.no_branch_index;
            if (noBranchIndex !== undefined && noBranchIndex !== null) {
                const targetId = nodeIdsByIndex[noBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-no-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'no',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: 'No',
                    });
                }
            }
        }

        else if (node.type === 'ivr_call') {
            const nextNodeIndex = node.next_node_index;
            if (nextNodeIndex !== undefined && nextNodeIndex !== null && nextNodeIndex !== -1) {
                const targetId = nodeIdsByIndex[nextNodeIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                    });
                }
            }
        }

        else if (node.type === 'call_flow') {
            const nextNodeIndex = node.next_node_index;
            if (nextNodeIndex !== undefined && nextNodeIndex !== null && nextNodeIndex !== -1) {
                const targetId = nodeIdsByIndex[nextNodeIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                    });
                }
            }
        }

        else if (node.type === 'whatsapp_call_flow') {
            const completedIndex = node.config?.completed_branch_index;
            if (completedIndex !== undefined && completedIndex !== null && completedIndex !== -1) {
                const targetId = nodeIdsByIndex[completedIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-${targetId}-completed`,
                        source: sourceNodeId,
                        sourceHandle: 'completed',
                        target: targetId,
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                    });
                }
            }

            if (node.config?.timeout_enabled) {
                const timeoutIndex = node.config?.timeout_branch_index;
                if (timeoutIndex !== undefined && timeoutIndex !== null && timeoutIndex !== -1) {
                    const targetId = nodeIdsByIndex[timeoutIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-${targetId}-timeout`,
                            source: sourceNodeId,
                            sourceHandle: 'timeout',
                            target: targetId,
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                        });
                    }
                }
            }
        }

        else if (node.type === 'customer_profile') {
            // True / yes branch
            const yesBranchIndex = node.config?.yes_branch_index;
            if (
                yesBranchIndex !== undefined &&
                yesBranchIndex !== null &&
                yesBranchIndex !== -1
            ) {
                const targetId = nodeIdsByIndex[yesBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-true-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'true',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: node.config?.true_label || 'Match (True)',
                    });
                }
            }

            // False / no branch
            const noBranchIndex = node.config?.no_branch_index;
            if (
                noBranchIndex !== undefined &&
                noBranchIndex !== null &&
                noBranchIndex !== -1
            ) {
                const targetId = nodeIdsByIndex[noBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-false-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'false',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: node.config?.false_label || 'No Match (False)',
                    });
                }
            }
        }
        else if (node.type === 'check_channel_consent') {
            // Enabled branch
            const enabledBranchIndex = node.config?.enabled_branch_index;
            if (
                enabledBranchIndex !== undefined &&
                enabledBranchIndex !== null &&
                enabledBranchIndex !== -1
            ) {
                const targetId = nodeIdsByIndex[enabledBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-enabled-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'enabled',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: node.config?.enabled_label || 'Enabled',
                    });
                }
            }

            // Disabled branch
            const disabledBranchIndex = node.config?.disabled_branch_index;
            if (
                disabledBranchIndex !== undefined &&
                disabledBranchIndex !== null &&
                disabledBranchIndex !== -1
            ) {
                const targetId = nodeIdsByIndex[disabledBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-disabled-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'disabled',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: node.config?.disabled_label || 'Disabled',
                    });
                }
            }
        }
        else if (node.type === 'smart_condition') {
            const groups = node.config?.groups || [];
            groups.forEach(group => {
                const branchIndex = group.branch_index;
                if (branchIndex !== undefined && branchIndex !== null && branchIndex !== -1) {
                    const targetId = nodeIdsByIndex[branchIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-group-${group.id}-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            sourceHandle: `group-${group.id}`,
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                            label: group.label,
                        });
                    }
                }
            });

            const defaultBranchIndex = node.config?.default_branch_index;
            if (defaultBranchIndex !== undefined && defaultBranchIndex !== null && defaultBranchIndex !== -1) {
                const targetId = nodeIdsByIndex[defaultBranchIndex];
                if (targetId) {
                    reactFlowEdges.push({
                        id: `edge-${sourceNodeId}-default-${targetId}`,
                        source: sourceNodeId,
                        target: targetId,
                        sourceHandle: 'default',
                        type: 'smoothstep',
                        animated: true,
                        markerEnd: { type: MarkerType.ArrowClosed },
                        label: node.config?.default_label || 'No Match',
                    });
                }
            }
        }

        else if (node.type !== 'end') {
            // For non-condition and non-end nodes, use next_node_index
            const nextNodeIndex = node.next_node_index;
            if (nextNodeIndex !== undefined && nextNodeIndex !== null && nextNodeIndex !== -1) {
                // Skip edges whose target is a merge node — those are reconstructed
                // from merge.config.branches so they land on the correct merge-in-N handle
                const targetNode = backendFlow.nodes[nextNodeIndex];
                if (targetNode?.type !== 'merge') {
                    const targetId = nodeIdsByIndex[nextNodeIndex];
                    if (targetId) {
                        reactFlowEdges.push({
                            id: `edge-${sourceNodeId}-${targetId}`,
                            source: sourceNodeId,
                            target: targetId,
                            type: 'smoothstep',
                            animated: true,
                            markerEnd: { type: MarkerType.ArrowClosed },
                        });
                    }
                }
            }
        }


    });

    // Deduplicate edges: if two edges share the same source+sourceHandle+target,
    // prefer the one with a targetHandle (merge node creates these; source nodes don't)
    const edgeMap = new Map();
    reactFlowEdges.forEach(e => {
        const key = `${e.source}|${e.sourceHandle ?? ''}|${e.target}`;
        const existing = edgeMap.get(key);
        if (!existing || (e.targetHandle && !existing.targetHandle)) {
            edgeMap.set(key, e);
        }
    });

    // Colour every edge from one palette, keyed off its sourceHandle and source node
    // type, rather than trusting the `style` each push above set for itself — those had
    // drifted into three greens and three reds for the same yes/no branches. See
    // utils/edgeStyles.js.
    return {
        nodes: reactFlowNodes,
        edges: withEdgeStyles(Array.from(edgeMap.values()), reactFlowNodes),
    };
};